import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { DateTime } from 'luxon';
import {
    Box,
    Button,
    Card, CardActions,
    CardContent,
    CardHeader, Checkbox, FormControlLabel, Grid, IconButton, Link, MenuItem,
    Table, TableBody, TableCell,
    TableContainer, TableHead,
    TableRow, TextField,
} from '@mui/material';
import { Add, Delete, ThumbUpAlt } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import CurrencyFormatField from 'components/common/CurrencyFormatField';
import AutoSearch, { Option } from 'components/common/AutoSearch';
import Currency from 'components/formaters/Currency';
import { InvoiceModel } from 'models/InvoiceModel';
import { InvoiceRenderLink } from 'modules/invoices/common/Components';
import useInvoiceModals from 'modules/invoices/hooks/useInvoiceModals';
import {
    CommonInvoiceableTableProps,
    FreightInvoiceableList,
    FreightInvoiceableTable,
    CommonInvoiceableList as CIL,
    CommonInvoiceableTable as CIT,
} from 'modules/invoices/common/InvoiceView';
import Api from 'utils/api';


type InvoiceProps = {
    invoice?: InvoiceModel,
    confirm?: () => void,
    remove?: () => void,
    update: (invoice: InvoiceModel) => void,
    save?: () => void,
    load?: () => void,
}

const InvoiceForm = ({invoice, confirm, remove, update, save, load}: InvoiceProps) => {
    const {t} = useTranslation();
    const modals = useInvoiceModals();
    const [created, setCreated] = useState<DateTime>(invoice?.createdAt ? DateTime.fromISO(invoice.createdAt) : DateTime.now());
    const [due, setDue] = useState<DateTime>(invoice?.dueDate ? DateTime.fromISO(invoice.dueDate) : DateTime.now().plus({days: 7}));
    const [payer, setPayer] = useState<Partial<InvoiceModel['payer']>>(invoice?.payer || {
        type: 'shipper',
    });

    let Items: any;

    if (isMobile) {
        Items = invoice?.type === 'freight' ?
            FreightInvoiceableList :
            CIL;
    } else {
        Items = invoice?.type === 'freight' ?
            FreightInvoiceableTable :
            CIT;
    }

    const search = payer.type === 'shipper' ? Api.company.search : Api.parties.search;

    const addPayer = (val: Option | null) => {
        if (val) setPayer(old => ({...old, id: val.id}))
    };

    const status = invoice?.status ? `(${t(`status_${invoice?.status}`)})` : '';

    return (
        <Card sx={{my: isMobile ? undefined : 2}}>
            <CardHeader
                title={`${t('invoice')} №${invoice?.number || DateTime.now().toFormat('yyyyMM') + '-xxx'} ${status}`}
                action={<>
                    {invoice && (
                        <InvoiceRenderLink invoice={invoice}/>
                    )}

                    {confirm && (
                        <IconButton onClick={confirm}>
                            <ThumbUpAlt/>
                        </IconButton>
                    )}

                    {remove && (
                        <IconButton onClick={remove}>
                            <Delete/>
                        </IconButton>
                    )}
                </>}
            />
            <CardContent>
                <Grid container columnSpacing={2} alignItems="flex-end">
                    {invoice && (
                        <>
                            <Grid item xs={12} sm>
                                {t(`invoice_${invoice.type}`)}
                                {invoice.type === 'freight' && (
                                    <>
                                        {' '}
                                        <Link href={`/voyage/${invoice.voyage.id}/finances`}>{invoice.voyage.number}</Link>
                                    </>
                                )}
                            </Grid>
                            <Grid item xs={12} sm>
                                {invoice.payer.name}
                            </Grid>
                        </>
                    )}
                    {!invoice && (
                        <>
                            <Grid item xs={12} sm>
                                <TextField
                                    select
                                    id="payerType"
                                    label={t('payer_type')}
                                    fullWidth
                                    margin="normal"
                                    variant="outlined"
                                    value={payer.type}
                                    onChange={e => setPayer({type: e.target.value})}
                                >
                                    <MenuItem value="shipper">{t('shipper')}</MenuItem>
                                    <MenuItem value="consignee">{t('consignee')}</MenuItem>
                                </TextField>
                            </Grid>
                            <Grid item xs={12} sm>
                                <AutoSearch
                                    key={payer.type}
                                    search={search}
                                    onChange={addPayer}
                                    margin="normal"
                                    label={t('payer')}
                                    keepValue
                                />
                            </Grid>
                        </>
                    )}

                    <Grid item xs={12} sm>
                        <DatePicker
                            label={t('issue_date')}
                            format="yyyy-MM-dd"
                            slotProps={{textField: {fullWidth: true, required: true}}}
                            onChange={d => setCreated(d!)}
                            value={created}
                        />
                    </Grid>
                    <Grid item xs={12} sm>
                        <DatePicker
                            label={t('due_date')}
                            format="yyyy-MM-dd"
                            slotProps={{textField: {fullWidth: true, required: true}}}
                            onChange={d => setDue(d!)}
                            value={due}
                        />
                    </Grid>
                </Grid>
            </CardContent>

            <TableContainer>
                <Items
                    items={invoice?.invoiceables || []}
                    add={() => {
                    }}
                    remove={(i: number) => modals.confirmRemoveFromInvoice(invoice!.invoiceables[i].name, 'main', invoice!.invoiceables[i].id).then(load)}
                />
            </TableContainer>

            {invoice &&
            <Grid container justifyContent="flex-end">
                <Grid item sm={4}>
                    <TableContainer>
                        <Table size="small">
                            <TableBody>
                                <TableRow>
                                    <TableCell sx={{fontWeight: 'bold'}}>{t('total_due')}:</TableCell>
                                    <TableCell align="right" sx={{fontWeight: 'bold'}}>
                                        <Currency value={invoice.total}/>
                                    </TableCell>
                                </TableRow>
                                {!!invoice?.totalTax && (
                                    <TableRow>
                                        <TableCell sx={{fontWeight: 'bold'}}>{t('tax')}:</TableCell>
                                        <TableCell align="right" sx={{fontWeight: 'bold'}}>
                                            <Currency value={invoice.totalTax}/>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!!invoice.prepaid && (
                                    <TableRow>
                                        <TableCell sx={{fontWeight: 'bold'}}>{t('advance_payment')}:</TableCell>
                                        <TableCell align="right" sx={{fontWeight: 'bold'}}>
                                            <Currency value={invoice.prepaid}/>
                                        </TableCell>
                                    </TableRow>
                                )}
                                {(!!invoice.prepaid || !!invoice.totalTax) && (
                                    <TableRow>
                                        <TableCell sx={{fontWeight: 'bold'}}>{t('grand_total')}:</TableCell>
                                        <TableCell align="right" sx={{fontWeight: 'bold'}}>
                                            <Currency value={invoice.grandTotal}/>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Grid>
            </Grid>
            }

            <CardContent>
                <TextField
                    id="note"
                    label={t('note')}
                    fullWidth
                    multiline
                    rows={4}
                    margin="normal"
                    variant="outlined"
                    value={invoice?.note}
                    onChange={e => setPayer({type: e.target.value})}
                />
            </CardContent>

            {save && (
                <CardActions sx={{justifyContent: 'flex-end'}}>
                    <Button onClick={save}>
                        {t('save')}
                    </Button>
                </CardActions>
            )}
        </Card>
    );
};


export const CommonInvoiceableTable = ({items, add, remove, setVal}: CommonInvoiceableTableProps) => {
    const {t} = useTranslation();

    if (!setVal)
        return null;

    return (
        <>
            <Table
                size="small"
                sx={{
                    '& .MuiTableCell-sizeSmall': {
                        padding: 0,
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell>#</TableCell>
                        <TableCell>{t('item_name')}</TableCell>
                        <TableCell>{t('description')}</TableCell>
                        <TableCell width="10%">{t('qty')}</TableCell>
                        <TableCell width="15%">{t('price')}</TableCell>
                        <TableCell width="15%">{t('plus_tax', {value: 10})}</TableCell>
                        {remove && <TableCell sx={{width: 0, whiteSpace: 'nowrap'}}></TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((item, i) => (
                        <TableRow key={i}>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>
                                <TextField
                                    required
                                    fullWidth
                                    margin="none"
                                    variant="outlined"
                                    value={item.name}
                                    onChange={e => setVal(i, 'name', e.target.value)}
                                />
                            </TableCell>

                            <TableCell>
                                <TextField
                                    fullWidth
                                    margin="none"
                                    variant="outlined"
                                    value={item.description}
                                    onChange={e => setVal(i, 'description', e.target.value)}
                                />
                            </TableCell>

                            <TableCell>
                                <TextField
                                    required
                                    fullWidth
                                    margin="none"
                                    variant="outlined"
                                    value={item.quantity}
                                    onChange={e => setVal(i, 'quantity', Number(e.target.value))}
                                    inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                                />
                            </TableCell>

                            <TableCell>
                                <TextField
                                    required
                                    fullWidth
                                    margin="none"
                                    variant="outlined"
                                    value={item.price}
                                    onChange={e => setVal(i, 'price', Number(e.target.value))}
                                    InputProps={{
                                        inputComponent: CurrencyFormatField as any,
                                    }}
                                />
                            </TableCell>

                            <TableCell>
                                <Checkbox
                                    name="tax"
                                    value={10}
                                    onChange={e => setVal(i, 'tax',e.target.checked ? 10 : 0)}
                                />
                            </TableCell>

                            {remove && (
                                <TableCell>
                                    <IconButton
                                        aria-label="delete"
                                        size="small"
                                        onClick={() => remove(i)}
                                    >
                                        <Delete/>
                                    </IconButton>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Box textAlign="right">
                <IconButton
                    aria-label="add"
                    size="small"
                    onClick={add}
                >
                    <Add/>
                </IconButton>
            </Box>
        </>
    )
};

export const CommonInvoiceableList = ({items, add, remove, setVal}: CommonInvoiceableTableProps) => {
    const {t} = useTranslation();

    if (!setVal)
        return null;

    return (
        <>
            <Table
                size="small"
                sx={{
                    '& .MuiTableCell-sizeSmall': {
                        padding: '3px',
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        {remove && <TableCell sx={{width: 0, whiteSpace: 'nowrap'}}></TableCell>}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((item, i) => (
                        <TableRow key={i}>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell>
                                <TextField
                                    label={t('item_name')}
                                    fullWidth
                                    variant="outlined"
                                    value={item.name}
                                    onChange={e => setVal(i, 'name', e.target.value)}
                                />

                                <TextField
                                    label={t('description')}
                                    fullWidth
                                    variant="outlined"
                                    value={item.description}
                                    onChange={e => setVal(i, 'description', e.target.value)}
                                />

                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <TextField
                                            label={t('quantity')}
                                            fullWidth
                                            variant="outlined"
                                            value={item.quantity}
                                            onChange={e => setVal(i, 'quantity', Number(e.target.value))}
                                            inputProps={{inputMode: 'numeric', pattern: '[0-9]*'}}
                                        />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField
                                            label={t('price')}
                                            fullWidth
                                            variant="outlined"
                                            value={item.price}
                                            onChange={e => setVal(i, 'price', Number(e.target.value))}
                                            InputProps={{
                                                inputComponent: CurrencyFormatField as any,
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </TableCell>

                            {remove && (
                                <TableCell>
                                    <IconButton
                                        aria-label="delete"
                                        size="small"
                                        onClick={() => remove(i)}
                                    >
                                        <Delete/>
                                    </IconButton>
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Box textAlign="right">
                <IconButton
                    aria-label="add"
                    size="small"
                    onClick={add}
                >
                    <Add/>
                </IconButton>
            </Box>
        </>
    )
};


export default InvoiceForm;
